import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import { pluralizeIssues } from '../../../../../../../../../../../../shared/helpers';
import { Badge, BadgeColors, BadgeShapes, Icon, icons } from '../../../../../../../../../../generic';
import List from '../../../../../../../../../../generic/List';
import { ProjectRowDetailsItem } from '../../../../../../../ProjectRowDetailsItem/ProjectRowDetailsItem';
import type { ProjectId, ProjectIssues, ProjectLabels, ProjectTeam } from '../../../../../../../../../../../../shared/projectsV2';
import type { SelectOption } from '../../../../../../../../../../../types';
import type { TeamSelectOption } from '../../../../../../../../../../shared/teams/hooks/useTeamsSelectOptions';

import styles from './ProjectRowDetails.module.less';

type ProjectIssueBadgeProps = {
  totalIssues: number | null;
};
/**
 * Badge component for displaying the total number of issues for a project or Unscanned.
 * Intended to be used in the ProjectRowDetails component.
 */
const ProjectIssueBadge = ({ totalIssues }: ProjectIssueBadgeProps) => (
  <Badge color={BadgeColors.LightGray} shape={BadgeShapes.Circle}>
    <div className={styles.issueCountLabel}>
      {_.isNil(totalIssues) ? (
        'Unscanned'
      ) : (
        <>
          <span className={styles.count}>{totalIssues}</span>
          {pluralizeIssues(totalIssues)}
        </>
      )}
    </div>
  </Badge>
);

type Props = {
  projectId: ProjectId;
  teams: ProjectTeam[];
  labels: ProjectLabels;
  issues: ProjectIssues;
  onLabelClick: (selected: SelectOption[]) => void;
  selectedLabels: SelectOption[];
  onTeamClick: (selected: TeamSelectOption[]) => void;
  selectedTeams: TeamSelectOption[];
};
/**
 * Component for displaying the details of a project in a row including teams, labels, and issues counts.
 */
const ProjectRowDetails: VoidFunctionComponent<Props> = (props) => {
  const projectTeams = props.teams.map((team, idx) => (
    <ProjectRowDetailsItem
      item={{ label: team.name, value: team.id }}
      index={idx}
      itemCount={props.teams.length}
      key={`projectRowToolTip-${props.projectId}-${team.name}`}
      onFilterChange={props.onTeamClick as (selected: Array<SelectOption | TeamSelectOption>) => void}
      selectedItems={props.selectedTeams}
    />
  ));

  const projectLabels = props.labels.map((label, idx) => (
    <ProjectRowDetailsItem
      item={{ label, value: label }}
      index={idx}
      itemCount={props.labels.length}
      key={`projectRowToolTip-${label}-${props.projectId}`}
      onFilterChange={props.onLabelClick as (selected: Array<SelectOption | TeamSelectOption>) => void}
      selectedItems={props.selectedLabels}
    />
  ));

  return (
    <List.RowDetails key={`project-row-details-${props.projectId}`} shouldWrap>
      {!_.isEmpty(props.teams) && (
        <>
          <span className={styles.detailIcon}>
            <Icon icon={icons.userGroup} />
          </span>
          <span className={styles.detailCollection}>{projectTeams}</span>
        </>
      )}
      {!_.isEmpty(props.labels) && (
        <>
          <span className={styles.detailIcon}>
            <Icon icon={icons.tags} />
          </span>
          <span className={styles.detailCollection}>{projectLabels}</span>
        </>
      )}
      <List.RowDetailsItem key={`project-row-details-item-${props.projectId}`}>
        <ProjectIssueBadge totalIssues={props.issues.total} />
      </List.RowDetailsItem>
    </List.RowDetails>
  );
};

export default ProjectRowDetails;
